// A team invoice (finance-real, the user 2026-10-22: "for a team the modal just shows each job with their claims and also
// allow to claim any other completed jobs, or add new jobs too"): one person's invoice to the agency, a collection of their
// claims, named in the URL (?modal=team-invoice&teamInvoice=<id>, DD-045, DD-051) and opened on any /finance tab, as
// To do's Team to pay row opens it. Each claim is the shared JobLine (its job, client and period, its amount); on a draft it
// is pressed open in place as the shared claim form (ClaimForm, as the job drawer's claims are): its amount saved, Take
// off the invoice, and "Open the job" for its job's drawer in place of this one, on the tab the user is on; a held one
// (received or paid) shows its
// lock, and opens its job. What they add up to, Claimed; its status the shared status dropdown (DD-062): Draft and
// Received are picked (Received
// stamps the day), and Paid opens a footer step recording the payout, the whole of it, which locks its claims. A draft
// takes the person's other open claims, claims another job (Active, Paused, Done or Invoiced with room under its cap, from any
// client, refused over what is free), or adds a new job: the job form stacks one level over it (DD-045's create flow,
// `stack=line-job-form`), the client picked first, and makes the job active with the claim on it. Its figures are the
// server's (TD-102). Claim another job is the shared claim form (ClaimForm) for the job picked, the person fixed, the
// share of its price and what is free shown with the amount (the user 2026-10-23); its picker's jobs are JobLines with
// their status pill. A job the person already claims reads "yours: <amount>", and claiming it adds to that claim: one
// claim per person per job. The payout sends a request key (useRequestKeys), so a retry never pays out twice (TD-077).
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { DayField } from "@/components/ui/DayField";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG, EntityCreateButton } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { LineJobFormDrawer } from "../clientPage/LineJobForms";
import { JOB_STATUS, TEAM_INVOICE_STATES, TEAM_INVOICE_STATUSES } from "../clientPage/lineJobText";
import { StatusBadge, StatusPill } from "../clientPage/StatusBadge";
import { ClaimForm, EscapeScope, type ClaimJob } from "../components/ClaimForm";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { JobLine } from "../components/JobLine";
import { useRequestKeys, type RequestKeys } from "../hooks/useRequestKeys";
import type { ClaimableJob, LineJobStatus, TeamInvoice, TeamInvoiceClaim, TeamInvoiceStatus } from "../types";
import { closeUnlessPending } from "../utils/mutations";
import { claimsCount, clientName, invoiceDateLine, jobLabel, jobSubline, NO_CLAIM } from "../utils/names";
import { useTeamInvoiceDrawer, type TeamInvoiceDrawerModel } from "./hooks/useTeamInvoiceDrawer";

const TEAM = ENTITY_CONFIG.teamInvoice;
// `contract` names the user's job now (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();
const heading = "text-[11px] uppercase tracking-wide text-gray-500 mb-1";
/** Every new claim's verb (DD-056), the job drawer's too. */
const ADD_CLAIM = "Add the claim";

/** A job a team invoice's claim can still change on: Active, Paused, Done or Invoiced (the one claim rule). */
const claimsOpen = (status: LineJobStatus): boolean => status === "active" || status === "paused" || status === "done" || status === "invoiced";

/** A saved claim's job as its form reads it. The form takes a saved claim's room from its `maxMinor` (freeFor), never the
 * job's free room, which TeamInvoiceClaim does not carry: 0 here, so nothing reads it as room. */
const savedClaimJob = (c: TeamInvoiceClaim, label: string): ClaimJob => ({ id: c.jobId, label, priceMinor: c.priceMinor, marginPct: c.marginPct, capMinor: c.capMinor, freeMinor: 0 });

/** A team invoice's claims: each a JobLine; on a draft, pressed open in place as the shared claim form (ClaimsList's way):
 * its amount saved, Take off the invoice, Open the job. Escape or Cancel closes it alone and gives focus back to its row. */
function TeamClaims({ invoice, draft, busy, remove, updateClaim, onOpenJob, nameOf }: {
  invoice: TeamInvoice;
  draft: boolean;
  busy: boolean;
  remove: TeamInvoiceDrawerModel["remove"];
  updateClaim: TeamInvoiceDrawerModel["updateClaim"];
  onOpenJob: (jobId: string) => void;
  /** A client as the drawer names it: Agency for the own client. */
  nameOf: (client: { id: string; name: string }) => string;
}) {
  const [opened, setOpened] = useState<string | null>(null);
  const back = useRef<string | null>(null);
  const list = useRef<HTMLUListElement>(null);
  useEffect(() => {
    if (opened !== null || back.current === null) return;
    list.current?.querySelector<HTMLElement>(`[data-claim-focus="${back.current}"]`)?.focus();
    back.current = null;
  }, [opened]);
  const open = (c: TeamInvoiceClaim) => {
    updateClaim.reset();
    remove.reset();
    setOpened(c.claimId);
  };
  const shut = (claimId: string | null) => {
    back.current = claimId;
    setOpened(null);
  };
  if (invoice.claims.length === 0) return <p className="text-gray-400">{NO_CLAIM}</p>;
  return (
    <ul ref={list} className="divide-y divide-gray-100">
      {invoice.claims.map((c) => {
        const label = jobLabel(c.jobName, c.periodLabel);
        const canChange = claimsOpen(c.jobStatus);
        return (
          <li key={c.claimId}>
            {opened === c.claimId ? (
              <div className="my-1">
                <ClaimForm
                  job={savedClaimJob(c, label)}
                  saved={{ personId: invoice.person.id, amountMinor: c.amountMinor, maxMinor: c.maxMinor, overCapMinor: c.overCapMinor }}
                  heading={label}
                  subheading={nameOf(c.client)}
                  headingAside={
                    <Button size="sm" variant="ghost" disabled={busy} onClick={() => onOpenJob(c.jobId)}>
                      {`Open the ${jobWord}`}
                    </Button>
                  }
                  verb="Save"
                  canSubmit={canChange}
                  note={canChange ? undefined : `${label} takes no claim now: the claim can only be taken off.`}
                  pending={updateClaim.isPending}
                  removing={remove.isPending}
                  refused={updateClaim.error?.message ?? remove.error?.message}
                  onSubmit={(d) => updateClaim.mutate({ claimId: c.claimId, input: { personId: invoice.person.id, amountMinor: d.amountMinor } }, { onSuccess: () => shut(c.claimId) })}
                  onRemove={() => remove.mutate({ invoiceId: invoice.id, claimId: c.claimId }, { onSuccess: () => shut(null) })}
                  onCancel={() => shut(c.claimId)}
                />
              </div>
            ) : (
              <JobLine
                name={c.jobName}
                sub={jobSubline(c, nameOf(c.client))}
                note={c.lock}
                amountMinor={c.amountMinor}
                focusKey={c.claimId}
                ariaLabel={draft ? `Change the claim on ${label}` : `Open ${label}`}
                // A held claim no longer changes: its row opens its job.
                onOpen={() => (draft ? open(c) : onOpenJob(c.jobId))}
                trailing={<StatusPill look={JOB_STATUS[c.jobStatus]} />}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface StatusProps {
  invoice: TeamInvoice;
  onPick: (status: TeamInvoiceStatus) => void;
  loading?: boolean;
  align?: "left" | "right";
}

/** A team invoice's status dropdown, on its row and in its drawer (DD-062). Draft and Received while it is not paid
 * (Received once it holds a claim); Paid pays a received one out. A paid one holds. */
export function TeamInvoiceStatusBadge({ invoice, onPick, loading = false, align = "left" }: StatusProps) {
  const paid = invoice.status === "paid";
  const open = (s: TeamInvoiceStatus): boolean => {
    if (s === "paid") return invoice.status === "received";
    if (s === "received") return !paid && invoice.claims.length > 0;
    return !paid;
  };
  return (
    <StatusBadge<TeamInvoiceStatus>
      value={invoice.status}
      choices={TEAM_INVOICE_STATUSES.map((s) => ({ value: s, look: TEAM_INVOICE_STATES[s], disabled: !open(s) }))}
      onPick={onPick}
      loading={loading}
      align={align}
      ariaLabel={`${invoice.person.name} · ${invoice.title} status`}
    />
  );
}

/** The footer step Paid opens: the day it was paid out. The amount is the whole of it: its claims are what it pays. Each
 * attempt sends one request key, kept on its retry. */
function PayoutStep({ invoice, today, pay, keys, onDone }: { invoice: TeamInvoice; today: string; pay: TeamInvoiceDrawerModel["pay"]; keys: RequestKeys; onDone: () => void }) {
  const [paidOn, setPaidOn] = useState(today);
  const scope = `${invoice.id}:payout`;
  const record = () => {
    if (paidOn === "") return;
    pay.mutate(
      { invoiceId: invoice.id, input: { paidOn, requestId: keys.keyFor(scope) } },
      {
        onSuccess: () => {
          keys.settled(scope);
          onDone();
        },
        onError: (error) => keys.settled(scope, error),
      },
    );
  };
  return (
    <div className="w-full space-y-3">
      <p className="text-sm text-gray-700">
        {`Pay ${invoice.person.name} out: `}
        <MoneyText amountMinor={invoice.totalMinor} strong />
        {`, its ${claimsCount(invoice.claims.length)}. They are locked once paid.`}
      </p>
      <FormField label="Paid out on">
        <DayField ariaLabel="Paid out on" value={paidOn} onChange={setPaidOn} />
      </FormField>
      {pay.error && <p className="text-xs text-red-600">{pay.error.message}</p>}
      <div className="flex justify-end gap-3">
        <Button variant="secondary" disabled={pay.isPending} onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={paidOn === "" || pay.isPending} isLoading={pay.isPending} onClick={record}>
          {`Record the payout · ${formatMoney(invoice.totalMinor, "EUR")}`}
        </Button>
      </div>
    </div>
  );
}

interface ClaimAnotherProps {
  jobs: ClaimableJob[];
  /** A client as the list names it: Agency for the own client, when the caller knows which it is. */
  nameOf?: (client: { id: string; name: string }) => string;
  /** Makes the claim; resolves once it is made, and rejects with the server's refusal. */
  onClaim: (job: ClaimableJob, amountMinor: number) => Promise<unknown>;
  pending: boolean;
  error: Error | null;
  onReset: () => void;
  onDone: () => void;
}

/** Claim another job: a job picked from a filtered list, then the claim form for it (ClaimForm), what is free filled in,
 * the person fixed. The same rule as the job drawer's Add a claim (Active, Paused, Done or Invoiced with room under its cap),
 * the server's. Escape anywhere in it closes it alone. */
/** Its toggle's mark: closing it (Escape, Cancel, a claim made) gives focus back to that button. */
export const CLAIM_ANOTHER = "claim-another";

export function ClaimAnother({ jobs, nameOf = (c) => c.name, onClaim, pending, error, onReset, onDone }: ClaimAnotherProps) {
  const [filter, setFilter] = useState("");
  // Closed, the drawer renders Claim another job in its place in the same commit: focus goes back to it.
  useEffect(
    () => () => {
      window.setTimeout(() => document.querySelector<HTMLElement>(`[data-claim-focus="${CLAIM_ANOTHER}"]`)?.focus(), 0);
    },
    [],
  );
  const [picked, setPicked] = useState<ClaimableJob | null>(null);
  const words = filter.trim().toLowerCase();
  const shown = jobs.filter((j) => words === "" || `${j.name} ${nameOf(j.client)} ${j.periodLabel}`.toLowerCase().includes(words));
  const pick = (j: ClaimableJob) => {
    setPicked(j);
    onReset();
  };
  return (
    <EscapeScope onEscape={() => !pending && onDone()} className="space-y-3 rounded-lg bg-gray-50 p-3">
      <input
        aria-label={`Find a ${jobWord}`}
        className={inputClassName}
        value={filter}
        autoFocus
        placeholder={`Find a ${jobWord}, a ${ENTITY_CONFIG.client.label.toLowerCase()} or a month`}
        onChange={(e) => setFilter(e.target.value)}
      />
      {shown.length === 0 ? (
        <p className="text-xs text-gray-500">{`No active, paused, done or invoiced ${jobWord} with room under its cap${words ? " matches" : ""}.`}</p>
      ) : (
        <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100 rounded-md bg-white">
          {shown.map((j) => (
            <li key={j.jobId}>
              <span className="block px-2">
                <JobLine
                  name={j.name}
                  sub={jobSubline(j, nameOf(j.client))}
                  pressed={picked?.jobId === j.jobId}
                  onOpen={() => pick(j)}
                  aside={
                    <span className="shrink-0 text-right text-xs text-gray-500">
                      {"Free to claim: "}
                      <MoneyText amountMinor={j.freeMinor} />
                      {/* One claim per person per job: a claim here adds to theirs. */}
                      {j.yoursMinor > 0 && (
                        <span className="block">
                          {"yours: "}
                          <MoneyText amountMinor={j.yoursMinor} />
                        </span>
                      )}
                    </span>
                  }
                  trailing={<StatusPill look={JOB_STATUS[j.status]} />}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
      {picked ? (
        <ClaimForm
          key={picked.jobId}
          job={{ id: picked.jobId, label: jobLabel(picked.name, picked.periodLabel), priceMinor: picked.priceMinor, marginPct: picked.marginPct, capMinor: picked.capMinor, freeMinor: picked.freeMinor }}
          heading={`${jobLabel(picked.name, picked.periodLabel)}${picked.yoursMinor > 0 ? `: adds to the ${formatMoney(picked.yoursMinor, "EUR")} claimed` : ""}`}
          subheading={nameOf(picked.client)}
          initialAmountMinor={picked.freeMinor}
          verb={ADD_CLAIM}
          pending={pending}
          refused={error?.message}
          onSubmit={(d) => void onClaim(picked, d.amountMinor).then(onDone, () => undefined)}
          onCancel={onDone}
        />
      ) : (
        // DD-056: what Add the claim waits for, said before it shows.
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-gray-500">{`${ADD_CLAIM} needs a ${jobWord} picked above.`}</p>
          <Button size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        </div>
      )}
    </EscapeScope>
  );
}

interface Props {
  invoiceId: string;
  /** Opened from a row's status dropdown picking Paid: the payout step at once. */
  paying: boolean;
  /** The job form stacked over it (`stack=line-job-form`). */
  addingJob: boolean;
  /** The job form starts on the agency's own client: opened from the person's New job (`client=agency`). */
  onAgency?: boolean;
  onAddJob: (open: boolean) => void;
  /** Its job's drawer over the tab the user is on, in place of this one (DD-045). */
  onOpenJob: (jobId: string) => void;
  onClose: () => void;
}

export function TeamInvoiceDrawer({ invoiceId, paying: payingFirst, addingJob, onAgency = false, onAddJob, onOpenJob, onClose }: Props) {
  const { detail, remove, addOpen, claim, updateClaim, setStatus, pay } = useTeamInvoiceDrawer(invoiceId);
  const [paying, setPaying] = useState(payingFirst);
  const [claiming, setClaiming] = useState(false);
  const keys = useRequestKeys();
  const data = detail.data;
  const busy = remove.isPending || addOpen.isPending || claim.isPending || updateClaim.isPending || setStatus.isPending || pay.isPending;
  if (!data) {
    return (
      <FinanceDrawer title={TEAM.label} onClose={onClose}>
        <p className="text-sm text-gray-500">{detail.isError ? detail.error.message : `Loading the ${TEAM.label.toLowerCase()}…`}</p>
      </FinanceDrawer>
    );
  }
  const invoice = data.invoice;
  const draft = invoice.status === "draft";
  // The own client is the one New job's clients flag (TD-105): its jobs read Agency here.
  const ownId = data.clients.find((c) => c.ownClient)?.id;
  const nameOf = (c: { id: string; name: string }): string => clientName(c.name, c.id === ownId);
  const pick = (status: TeamInvoiceStatus) => {
    if (status === "paid") return setPaying(true);
    setPaying(false);
    if (status === "draft" || status === "received") setStatus.mutate({ invoiceId: invoice.id, input: { status } });
  };
  return (
    <>
      <FinanceDrawer
        onClose={closeUnlessPending(busy, onClose)}
        title={`${invoice.person.name} · ${invoice.title}`}
        footer={paying && invoice.status === "received" ? <PayoutStep invoice={invoice} today={data.today} pay={pay} keys={keys} onDone={() => setPaying(false)} /> : undefined}
      >
        <div className="space-y-5 text-sm">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <TeamInvoiceStatusBadge invoice={invoice} onPick={pick} loading={setStatus.isPending} />
              <span className="text-xs text-gray-500">{invoiceDateLine({ kind: "team", ...invoice })}</span>
            </div>
            {draft && <p className="text-xs text-gray-500">{`Put ${invoice.person.name}'s claims together. Received stamps today; Paid pays it out and locks its claims.`}</p>}
            {invoice.status === "received" && (
              <p className="text-xs text-gray-500">{`${invoice.person.name}'s invoice arrived: its claims are held, here and on their jobs. Paid records the payout; Draft takes it back to change its claims.`}</p>
            )}
            {invoice.status === "paid" && <p className="text-xs text-gray-500">Paid out: its claims no longer change, here or on their jobs.</p>}
            {setStatus.error && <p className="text-xs text-red-600">{setStatus.error.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-gray-500">Claimed</div>
              <MoneyText amountMinor={invoice.totalMinor} strong />
            </div>
            <div>
              <div className="text-xs text-gray-500">{invoice.status === "paid" ? "Paid out" : "To pay out"}</div>
              <MoneyText amountMinor={invoice.status === "paid" ? invoice.paidMinor : invoice.dueMinor} strong />
            </div>
          </div>

          <section className="space-y-2">
            <h3 className={heading}>{`${JOB.plural} and ${invoice.person.name}'s claims`}</h3>
            <TeamClaims invoice={invoice} draft={draft} busy={busy} remove={remove} updateClaim={updateClaim} onOpenJob={onOpenJob} nameOf={nameOf} />
            {draft && data.openClaims.length > 0 && (
              <Button size="sm" variant="secondary" disabled={busy} isLoading={addOpen.isPending} onClick={() => addOpen.mutate({ invoiceId: invoice.id, claimIds: data.openClaims.map((c) => c.claimId) })}>
                {`Add ${invoice.person.name}'s ${data.openClaims.length} other open ${(data.openClaims.length === 1 ? ENTITY_CONFIG.claim.label : ENTITY_CONFIG.claim.plural).toLowerCase()}`}
              </Button>
            )}
          </section>

          {draft && (
            <section className="space-y-2">
              <h3 className={heading}>More</h3>
              {claiming ? (
                <ClaimAnother
                  jobs={data.claimable}
                  nameOf={nameOf}
                  onClaim={(job, amountMinor) => claim.mutateAsync({ invoiceId: invoice.id, input: { jobId: job.jobId, amountMinor } })}
                  pending={claim.isPending}
                  error={claim.error}
                  onReset={claim.reset}
                  onDone={() => setClaiming(false)}
                />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" disabled={busy} data-claim-focus={CLAIM_ANOTHER} onClick={() => setClaiming(true)}>
                    {`Claim another ${jobWord}`}
                  </Button>
                  <EntityCreateButton entity="contract" variant="secondary" onClick={() => onAddJob(true)} />
                </div>
              )}
            </section>
          )}
        </div>
      </FinanceDrawer>
      {draft && addingJob && (
        <LineJobFormDrawer
          clientId=""
          own={false}
          today={data.today}
          forTeam={{ invoiceId: invoice.id, personName: invoice.person.name, title: `${invoice.person.name} · ${invoice.title}`, clients: data.clients }}
          startOnAgency={onAgency}
          onSaved={() => onAddJob(false)}
          onClose={() => onAddJob(false)}
        />
      )}
    </>
  );
}
